import axios from "axios";
import { useEffect, useState } from "react";
import {
  CalendarClock,
  Save,
  Plus,
  Trash2,
  Loader2,
  Info,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const WEEKDAYS = [
  { id: 0, label: "Segunda" },
  { id: 1, label: "Terça" },
  { id: 2, label: "Quarta" },
  { id: 3, label: "Quinta" },
  { id: 4, label: "Sexta" },
  { id: 5, label: "Sábado" },
  { id: 6, label: "Domingo" },
];

const emptySlot = (weekday = 0) => ({
  weekday,
  start: "09:00",
  end: "12:00",
});

const AvailabilityPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    timezone: "America/Sao_Paulo",
    slot_duration_minutes: 45,
    buffer_minutes: 15,
    advance_days: 14,
    recurring_slots: [],
  });
  const [previewSlots, setPreviewSlots] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [me, setMe] = useState(null);

  useDocumentTitle("Minha agenda");

  const load = async () => {
    setLoading(true);
    try {
      const [avail, meRes] = await Promise.all([
        axios.get(`${API}/availability/my`),
        axios.get(`${API}/auth/me`),
      ]);
      setData({
        timezone: avail.data.timezone || "America/Sao_Paulo",
        slot_duration_minutes: avail.data.slot_duration_minutes || 45,
        buffer_minutes: avail.data.buffer_minutes || 15,
        advance_days: avail.data.advance_days || 14,
        recurring_slots: avail.data.recurring_slots || [],
      });
      setMe(meRes.data);
    } catch (err) {
      toast.error("Não foi possível carregar a disponibilidade");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadPreview = async (userId) => {
    if (!userId) return;
    setLoadingPreview(true);
    try {
      const res = await axios.get(
        `${API}/availability/${userId}/slots?limit=5`
      );
      setPreviewSlots(res.data || []);
    } catch {
      setPreviewSlots([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    if (me?.id) loadPreview(me.id);
  }, [me?.id, data.recurring_slots.length]);

  const addSlot = () =>
    setData((p) => ({ ...p, recurring_slots: [...p.recurring_slots, emptySlot()] }));

  const updateSlot = (idx, patch) =>
    setData((p) => ({
      ...p,
      recurring_slots: p.recurring_slots.map((s, i) =>
        i === idx ? { ...s, ...patch } : s
      ),
    }));

  const removeSlot = (idx) =>
    setData((p) => ({
      ...p,
      recurring_slots: p.recurring_slots.filter((_, i) => i !== idx),
    }));

  const handleSave = async () => {
    // Quick validation
    for (const s of data.recurring_slots) {
      if (!s.start || !s.end || s.start >= s.end) {
        toast.error("Há uma faixa de horário inválida");
        return;
      }
    }
    setSaving(true);
    try {
      await axios.put(`${API}/availability/my`, data);
      toast.success("Disponibilidade salva");
      if (me?.id) loadPreview(me.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const slotsByWeekday = WEEKDAYS.map((wd) => ({
    ...wd,
    slots: data.recurring_slots
      .map((s, idx) => ({ ...s, idx }))
      .filter((s) => s.weekday === wd.id),
  }));

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" /> Carregando…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="heading text-2xl font-bold text-slate-900 flex items-center gap-2">
          <CalendarClock className="w-6 h-6 text-brand-blue" />
          Minha agenda
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Defina sua disponibilidade semanal para entrevistas. A IA do WhatsApp
          oferece esses horários aos candidatos e marca automaticamente quando
          eles escolherem.
        </p>
      </div>

      {/* Config numéricos */}
      <section className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-sm font-semibold text-slate-900 mb-3">
          Configurações gerais
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Duração de cada entrevista</Label>
            <Select
              value={String(data.slot_duration_minutes)}
              onValueChange={(v) =>
                setData({ ...data, slot_duration_minutes: Number(v) })
              }
            >
              <SelectTrigger data-testid="slot-duration-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 minutos</SelectItem>
                <SelectItem value="45">45 minutos</SelectItem>
                <SelectItem value="60">60 minutos</SelectItem>
                <SelectItem value="90">90 minutos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Intervalo entre entrevistas</Label>
            <Select
              value={String(data.buffer_minutes)}
              onValueChange={(v) =>
                setData({ ...data, buffer_minutes: Number(v) })
              }
            >
              <SelectTrigger data-testid="buffer-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Sem intervalo</SelectItem>
                <SelectItem value="10">10 minutos</SelectItem>
                <SelectItem value="15">15 minutos</SelectItem>
                <SelectItem value="30">30 minutos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Janela de agendamento</Label>
            <Select
              value={String(data.advance_days)}
              onValueChange={(v) =>
                setData({ ...data, advance_days: Number(v) })
              }
            >
              <SelectTrigger data-testid="advance-days-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Próximos 7 dias</SelectItem>
                <SelectItem value="14">Próximos 14 dias</SelectItem>
                <SelectItem value="21">Próximos 21 dias</SelectItem>
                <SelectItem value="30">Próximos 30 dias</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Semana recorrente */}
      <section className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-900">
            Horários disponíveis na semana
          </h2>
          <Button
            size="sm"
            variant="outline"
            onClick={addSlot}
            data-testid="add-slot-button"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Adicionar faixa
          </Button>
        </div>

        {data.recurring_slots.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
            <Clock className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600">
              Nenhuma disponibilidade cadastrada. Adicione pelo menos uma faixa
              de horário para a IA começar a oferecer agendamentos.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {slotsByWeekday.map((wd) =>
              wd.slots.length === 0 ? null : (
                <div key={wd.id}>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    {wd.label}
                  </div>
                  <div className="space-y-1.5">
                    {wd.slots.map((s) => (
                      <div
                        key={s.idx}
                        data-testid={`slot-row-${s.idx}`}
                        className="flex items-center gap-2"
                      >
                        <Select
                          value={String(s.weekday)}
                          onValueChange={(v) =>
                            updateSlot(s.idx, { weekday: Number(v) })
                          }
                        >
                          <SelectTrigger className="w-[130px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {WEEKDAYS.map((w) => (
                              <SelectItem value={String(w.id)} key={w.id}>
                                {w.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="time"
                          value={s.start}
                          onChange={(e) =>
                            updateSlot(s.idx, { start: e.target.value })
                          }
                          className="w-[120px]"
                          data-testid={`slot-start-${s.idx}`}
                        />
                        <span className="text-slate-400">até</span>
                        <Input
                          type="time"
                          value={s.end}
                          onChange={(e) =>
                            updateSlot(s.idx, { end: e.target.value })
                          }
                          className="w-[120px]"
                          data-testid={`slot-end-${s.idx}`}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeSlot(s.idx)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`remove-slot-${s.idx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        )}

        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900 flex gap-2">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            A IA gera slots de <strong>{data.slot_duration_minutes} minutos</strong>{" "}
            dentro de cada faixa, com{" "}
            <strong>{data.buffer_minutes} minutos de intervalo</strong> entre
            entrevistas. Nos próximos {data.advance_days} dias.
          </span>
        </div>
      </section>

      {/* Preview */}
      <section className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-sm font-semibold text-slate-900 mb-3">
          Próximos horários que a IA vai oferecer
        </h2>
        {loadingPreview ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Calculando…
          </div>
        ) : previewSlots.length === 0 ? (
          <p className="text-sm text-slate-500">
            Salve sua disponibilidade para ver uma prévia dos horários.
          </p>
        ) : (
          <ul data-testid="preview-slots" className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {previewSlots.map((s, i) => (
              <li
                key={`slot-${s.label}-${i}`}
                className="text-sm bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5 text-emerald-900 font-medium"
              >
                {s.label}
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-brand-blue hover:bg-brand-blue-dark text-white shadow-lg"
          data-testid="save-availability-button"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-1.5" />
          )}
          Salvar disponibilidade
        </Button>
      </div>
    </div>
  );
};

export default AvailabilityPage;
